import type { ITransaction } from "@gen/app-query";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { GridColDef } from "@mui/x-data-grid";
import { useCallback } from "react";

import { DateTimeCell, ExpandCell } from "~/components/data_grid_cells";
import { StatusChip } from "~/components/status_chip";

import { SellerPayoutCell } from "./SellerPayoutCell";
import { TotalCell } from "./TotalCell";
import { UserCell } from "./UserCell";

interface StatusInfo {
  code: string;
  label: string;
  color: string;
  text_color: string;
}

interface Localization {
  locale: string;
  timeZone: string;
}

export const useCreateTransactionColumns = () => {
  const createTransactionColumns = useCallback(
    (
      statuses: StatusInfo[],
      localization: Localization,
    ): GridColDef<ITransaction>[] => {
      const statusMap = new Map(statuses.map((s) => [s.code, s]));

      return [
        {
          field: "id",
          headerName: "ID",
          minWidth: 100,
          sortable: false,
          renderCell: (params) => {
            const v2Id = params.row.v2_id;
            return (
              <Stack flexDirection="column" alignItems="flex-start">
                <Typography variant="body2">{v2Id ?? params.row.id}</Typography>
                {v2Id && (
                  <Typography variant="caption" color="text.secondary">
                    {params.row.id}
                  </Typography>
                )}
              </Stack>
            );
          },
        },
        {
          field: "created",
          headerName: "Created",
          minWidth: 100,
          sortable: false,
          renderCell: (params) => {
            const created = params.row.created;

            const [datePart, timePart] = created
              .toLocaleString(localization.locale, {
                timeZone: localization.timeZone,
              })
              .split(", ");

            return (
              <Stack flexDirection="column" alignItems="flex-start">
                <Typography variant="body2">{datePart}</Typography>
                {timePart && (
                  <Typography variant="caption" color="text.secondary">
                    {timePart}
                  </Typography>
                )}
              </Stack>
            );
          },
        },
        {
          field: "updated",
          headerName: "Updated",
          minWidth: 100,
          sortable: false,
          renderCell: (props) => (
            <DateTimeCell
              {...props}
              locale={localization.locale}
              options={{
                timeZone: localization.timeZone,
              }}
            />
          ),
        },
        {
          field: "status",
          headerName: "Status",
          minWidth: 220,
          renderCell: (params) => {
            const statusInfo = statusMap.get(params.row.status);

            if (!statusInfo) {
              return params.row.status;
            }

            return (
              <StatusChip label={statusInfo.label} color={statusInfo.color} />
            );
          },
          sortable: false,
        },
        {
          field: "buyer_id",
          headerName: "Buyer",
          minWidth: 208,
          renderCell: (params) => (
            <UserCell
              {...params}
              userId={params.row.buyer_id ?? ""}
              userTransactionRole="buyer"
            />
          ),
          sortable: false,
        },
        {
          field: "seller_id",
          headerName: "Seller",
          minWidth: 208,
          renderCell: (params) => (
            <UserCell
              {...params}
              userId={params.row.seller_id ?? ""}
              userTransactionRole="seller"
            />
          ),
          sortable: false,
        },
        {
          field: "total",
          headerName: "Total",
          minWidth: 140,
          renderCell: (params) => <TotalCell transactionData={params.row} />,
          sortable: false,
        },
        {
          field: "description",
          headerName: "Details",
          minWidth: 208,
          flex: 1,
          renderCell: ExpandCell,
          sortable: false,
        },
        {
          field: "amount_released",
          headerName: "Amount Released",
          minWidth: 140,
          sortable: false,
          renderCell: (params) => (
            <SellerPayoutCell transactionData={params.row} />
          ),
        },
      ];
    },
    [],
  );

  return createTransactionColumns;
};
